import { useMemo } from 'react';

import uniq from 'lodash/uniq';

import type { S12nRanking } from 'bundles/enroll-common/utils/mixAndMatchUtils';
import { adaptMixAndMatchS12nRanking } from 'bundles/nostos-external/adapters';
import useNostosExternalData from 'bundles/nostos-external/useNostosExternalData';

/**
 * Fetches the previous calendar month's paid conversion count per s12n slug (MONETIZE-3250) used to rank the
 * Mix & Match picker. Client-side only: the order is client-rendered, so there's nothing to hydrate.
 */
const useMixAndMatchS12nRanking = (
  slugs: Array<string>,
  skip: boolean
): { ranking: S12nRanking | undefined; isLoading: boolean } => {
  const slugsKey = uniq(slugs).sort().join(',');
  const keys = useMemo(() => (slugsKey ? slugsKey.split(',') : []), [slugsKey]);

  const { elements, loading } = useNostosExternalData({
    table: 'mix_and_match_s12n_ranking',
    keys,
    adapt: adaptMixAndMatchS12nRanking,
    skip,
    ssr: false,
  });

  const ranking = useMemo(
    () =>
      elements &&
      elements.reduce<S12nRanking>((acc, { id, content }) => {
        acc[id] = content.paidConversions;
        return acc;
      }, {}),
    [elements]
  );

  return { ranking, isLoading: !skip && loading };
};

export default useMixAndMatchS12nRanking;
